import React from 'react';

import AppsIcon from '@atlaskit/icon/core/apps';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { B400, N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { type RovoLogoProps } from '../../constants/icons';
import { isNounKey, type NounKeys, nouns } from '../../constants/nouns';
import { type ProductKeys, useProductConfigs } from '../../constants/products';

export type ProductIconProps = RovoLogoProps & {
	product?: ProductKeys;
	isSelected?: boolean;
	useSearchResultIcon?: boolean;
};

export type ProductOrNounIconProps = RovoLogoProps & {
	productOrNounKey: ProductKeys | NounKeys | undefined;
	isSelected?: boolean;
};

export const ProductIcon = ({
	product,
	size = 'large',
	isSelected = false,
	useSearchResultIcon = false,
	...props
}: ProductIconProps) => {
	const products = useProductConfigs();
	if (product) {
		const config = products[product];
		const Icon = (useSearchResultIcon && config.SearchResultIcon) || config.Icon;
		return <Icon {...props} size={size} shouldUseNewLogoDesign />;
	} else {
		return (
			<AppsIcon
				label=""
				spacing="spacious"
				color={isSelected ? token('color.icon.selected', B400) : token('color.icon', N500)}
			/>
		);
	}
};

type ProductOrNounOrShowMoreProos = {
	isShowMore?: boolean;
} & ProductOrNounIconProps;

export const IconDecider = ({
	isShowMore,
	productOrNounKey,
	size = 'large',
	isSelected = false,
	...props
}: ProductOrNounOrShowMoreProos) => {
	if (isShowMore) {
		return (
			<ShowMoreHorizontalIcon
				label=""
				spacing="spacious"
				color={isSelected ? token('color.icon.selected', B400) : token('color.icon', N500)}
			/>
		);
	}

	return ProductOrNounIcon({
		productOrNounKey,
		size,
		isSelected,
		...props,
	});
};

export const ProductOrNounIcon = ({
	productOrNounKey,
	size = 'large',
	isSelected = false,
	...props
}: ProductOrNounIconProps) => {
	const products = useProductConfigs();
	let Icon;
	if (productOrNounKey) {
		const isNounFilter = isNounKey(productOrNounKey);

		if (isNounFilter) {
			Icon = nouns[productOrNounKey].Icon;
		} else {
			Icon = products[productOrNounKey as ProductKeys].Icon;
		}

		return <Icon {...props} size={size} />;
	} else {
		return (
			<AppsIcon
				label=""
				spacing="spacious"
				color={isSelected ? token('color.icon.selected', B400) : token('color.icon', N500)}
			/>
		);
	}
};
